/**
 * @file InlineToast — banner inline temporal para feedback post-acción
 * (success, error, info). Reemplaza los `window.alert()` nativos que
 * romper el flujo de la app y no respetan el sistema de diseño.
 *
 * Diseño: layout horizontal (icon + texto + close opcional), tres
 * tonos brand-aligned, auto-dismiss configurable, role="status" o
 * "alert" según tono. Aria-live para que lectores anuncien.
 *
 * Uso:
 *   const toast = useToast();
 *   toast({ tone: "success", message: "NC creada", autoDismissMs: 5000 });
 *   toast({ tone: "error", message: "Falló la descarga: <razón>" });
 *
 * Para banners persistentes inline (no auto-dismiss), usar el
 * componente directamente sin el provider:
 *   <InlineToastBanner tone="info" message="..." />
 */
import {
  type JSX,
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useState,
} from "react";

import { Icon } from "../../../../../.packages/galaxia-pro";
import "./inline-toast.css";

export type ToastTone = "success" | "error" | "info" | "warning";

export interface ToastOptions {
  tone: ToastTone;
  message: ReactNode;
  /** Tiempo en ms antes de auto-dismiss. Default 5000 (success/info)
   *  o 8000 (error/warning). Pasa 0 para no auto-cerrar. */
  autoDismissMs?: number;
}

interface ToastInstance extends ToastOptions {
  id: number;
}

const ToastContext = createContext<((opts: ToastOptions) => void) | null>(null);

export function useToast(): (opts: ToastOptions) => void {
  const ctx = useContext(ToastContext);
  if (ctx == null) {
    throw new Error("useToast(): falta <ToastProvider> en un ancestor");
  }
  return ctx;
}

let nextId = 1;

export function ToastProvider({ children }: { readonly children: ReactNode }): JSX.Element {
  const [items, setItems] = useState<ToastInstance[]>([]);

  const dismiss = useCallback((id: number) => {
    setItems((arr) => arr.filter((t) => t.id !== id));
  }, []);

  const push = useCallback((opts: ToastOptions) => {
    const id = nextId++;
    setItems((arr) => [...arr, { ...opts, id }]);
    const ms =
      opts.autoDismissMs ??
      (opts.tone === "error" || opts.tone === "warning" ? 8000 : 5000);
    if (ms > 0) {
      setTimeout(() => dismiss(id), ms);
    }
  }, [dismiss]);

  return (
    <ToastContext.Provider value={push}>
      {children}
      <div
        className="inline-toast__stack"
        aria-live="polite"
        aria-atomic="false"
      >
        {items.map((t) => (
          <InlineToastBanner
            key={t.id}
            tone={t.tone}
            onDismiss={() => dismiss(t.id)}
          >
            {t.message}
          </InlineToastBanner>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

interface InlineToastBannerProps {
  readonly tone: ToastTone;
  readonly children: ReactNode;
  readonly onDismiss?: () => void;
}

/** Componente standalone — sin provider, para banners fijos en
 *  páginas (ej. el "ejecución firmada exitosamente" en /checklists). */
export function InlineToastBanner({
  tone,
  children,
  onDismiss,
}: InlineToastBannerProps): JSX.Element {
  const icon = TONE_ICON[tone];
  // role="alert" para errores/warnings (interrumpe lector); "status"
  // para success/info (no interrumpe).
  const role = tone === "error" || tone === "warning" ? "alert" : "status";

  return (
    <div role={role} className={`inline-toast inline-toast--${tone}`}>
      <Icon name={icon} size={16} color="currentColor" />
      <span className="inline-toast__msg">{children}</span>
      {onDismiss ? (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Cerrar mensaje"
          className="inline-toast__close"
        >
          <Icon name="x" size={14} color="currentColor" />
        </button>
      ) : null}
    </div>
  );
}

const TONE_ICON: Record<ToastTone, "check" | "bell"> = {
  success: "check",
  error: "bell",
  warning: "bell",
  info: "bell",
};
